/**
 * TreeMapColumn Component
 *
 * Represents a single column in the tree map.
 * Renders a swimlane for each parent sphere, which independently loads its own children.
 */

import React from 'react';
import { useTranslation } from 'localization';
import type { TreeMapColumnProps } from '../../organisms/TreeMap/types';
import { resolveLabel } from '../../../utils/resolveLabel';
import { resolveLevelConfig } from '../../organisms/TreeMap/defaults';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import TreeMapSwimlane from '../TreeMapSwimlane/TreeMapSwimlane';
import './TreeMapColumn.scss';

const TreeMapColumn: React.FC<TreeMapColumnProps> = ({
  level,
  levelIndex,
  viewConfig,
  baseConfig,
  parentSpheres,
  editable,
  onExpand,
  onSphereSelect,
  onSphereDrop,
  organizationId,
  schemas,
  apiClient,
  defaultPageSize = 5,
  contextSystemName,
  rootSphereId
}) => {
  const { t, i18n } = useTranslation();
  const levelLabel = resolveLabel(level.label, i18n.language);

  // This column's effective density (level override → view/base config → 'standard'),
  // not the flat board-wide `cardView` — a level override must affect only its column.
  const resolvedCardView = resolveLevelConfig({ base: baseConfig, view: viewConfig }, level.id).cardDensity;

  return (
    <div className="tree-map-column">
      <div className="tree-map-column__header">
        <div className="tree-map-column__title">
          {level.icon && <GlyphAtom glyph={level.icon} glyphColor={level.glyphColor} className="tree-map-column__icon" />}
          <h3>{levelLabel}</h3>
        </div>
      </div>

      <div className="tree-map-column__content">
        {/* Render swimlane for each parent sphere */}
        {parentSpheres.map((parentSphere) => (
          <TreeMapSwimlane
            key={parentSphere.id}
            parentSphere={parentSphere}
            level={level}
            viewConfig={viewConfig}
            baseConfig={baseConfig}
            editable={editable}
            cardView={resolvedCardView}
            organizationId={organizationId}
            schemas={schemas}
            apiClient={apiClient}
            defaultPageSize={defaultPageSize}
            contextSystemName={contextSystemName}
            rootSphereId={rootSphereId}
            onSphereDrop={(sphereId) => onSphereDrop(sphereId, parentSphere.id)}
            onAddClick={() => {
              // Sphere editor integration pending
            }}
            onAIClick={() => {
              // AI assistant integration pending
            }}
            onSphereSelect={onSphereSelect}
          />
        ))}

        {/* Empty state */}
        {parentSpheres.length === 0 && (
          <div className="tree-map-column__empty">
            <i className="pi pi-inbox" />
            <p>{t('treeMap.column.noPreviousLevelSpheres')}</p>
            <small>{t('treeMap.column.addSpheresHint', { levelLabel })}</small>
          </div>
        )}
      </div>
    </div>
  );
};

export default TreeMapColumn;
